import { useRef, useEffect } from 'react'
import { Download, CalendarRange, Filter, FileCheck2, TrendingUp, Users, ShoppingCart, RotateCcw } from 'lucide-react'
import {
  Chart, BarElement, LineElement, PointElement, LinearScale, CategoryScale,
  Filler, Tooltip, BarController, LineController, ArcElement, DoughnutController,
} from 'chart.js'

Chart.register(BarElement, LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip, BarController, LineController, ArcElement, DoughnutController)

const TT = {
  backgroundColor: 'rgba(8,11,20,.95)',
  titleColor: '#EFF2FB',
  bodyColor: '#8B96B3',
  borderColor: 'rgba(99,102,241,.4)',
  borderWidth: 1,
  padding: 10,
  cornerRadius: 8,
}

const SUMMARY = [
  { label:'Total Revenue',   value:'$128.4K', change:'+18.2%', icon: TrendingUp,   color: '#4F46E5', colorGlow: 'rgba(79,70,229,0.18)',trendColor: '#4F46E5',  bar:85, variant: 'indigo' },
  { label:'Subscriptions',   value:'15,122',  change:'+11.4%', icon: Users,         color: '#06B6D4', colorGlow: 'rgba(6,182,212,0.15)',    bar:72, trendColor: '#06B6D4', variant: 'cyan' },
  { label:'Avg Order Value', value:'$84.90',  change:'+3.1%',  icon: ShoppingCart,  color: '#10B981', colorGlow: 'rgba(16,185,129,0.15)',  bar:60, trendColor: '#10B981', variant: 'green' },
  { label:'Refund Rate',     value:'1.2%',    change:'-0.4%',  icon: RotateCcw,     color: '#F59E0B', colorGlow: 'rgba(245,158,11,0.15)',  bar:12, trendDir:'neutral', trendColor: '#F59E0B', variant: 'amber' },
]

export default function Report() {
  const barRef   = useRef(null); const barInst   = useRef(null)
  const donutRef = useRef(null); const donutInst = useRef(null)

  useEffect(() => {
    if (barRef.current) {
      barInst.current?.destroy()
      barInst.current = new Chart(barRef.current, {
        type: 'bar',
        data: {
          labels: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
          datasets: [
            { label:'Revenue',  data:[42,58,51,67,72,89,95,103,88,112,119,128], backgroundColor:'rgba(79,70,229,.8)', borderRadius:4, borderSkipped:false },
            { label:'Expenses', data:[28,32,30,38,42,50,55,60,52,68,72,78],     backgroundColor:'rgba(239,68,68,.5)', borderRadius:4, borderSkipped:false },
          ],
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend:{ display:false }, tooltip: TT },
          scales: {
            x: { ticks:{ color:'#4A5578', font:{size:11} }, grid:{ display:false } },
            y: { ticks:{ color:'#4A5578', font:{size:11} }, grid:{ color:'rgba(255,255,255,.03)' } },
          },
        },
      })
    }
    if (donutRef.current) {
      donutInst.current?.destroy()
      donutInst.current = new Chart(donutRef.current, {
        type: 'doughnut',
        data: {
          labels: ['Enterprise','Pro','Starter','Free'],
          datasets: [{ data:[28,42,22,8], backgroundColor:['#4F46E5','#06B6D4','#10B981','#475569'], borderColor:'transparent', hoverOffset:6 }],
        },
        options: { responsive:true, maintainAspectRatio:false, cutout:'72%', plugins:{ legend:{display:false}, tooltip:TT } },
      })
    }
    return () => { barInst.current?.destroy(); donutInst.current?.destroy() }
  }, [])

  return (
    <div className="dash-page page-in">
      <section className="report-hero card">
        <div className="report-hero-glow"/>
        <div>
          <p className="report-overline">Reports Center</p>
          <h1 className="page-title" style={{ marginBottom:4 }}>Business Reports</h1>
          <p style={{ color:'var(--text3)', fontSize:'var(--fs-sm)' }}>Generate financial, plan, and growth reports in one place.</p>
        </div>
        <div style={{ display:'flex', gap:'var(--space-sm)', flexWrap:'wrap' }}>
          <button className="btn btn-outline"><CalendarRange size={14}/> Date range</button>
          <button className="btn btn-outline"><Filter size={14}/> Filters</button>
          <button className="btn btn-primary"><Download size={14}/> Export Report</button>
        </div>
      </section>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'var(--space-md)' }}>
        <h3 style={{ color:'var(--text1)', fontSize:'var(--fs-lg)', fontWeight:600 }}>Performance Summary</h3>
        <span className="badge badge-info"><FileCheck2 size={12}/> Updated 5 min ago</span>
      </div>

      <div className="stats-grid cols-4">
        {SUMMARY.map(s => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span style={{"position": "absolute", "top": "0px", "right": "0px", "width": "110px", "height": "110px", "border-radius": "50%", "background": s.colorGlow  , "filter": "blur(30px)", "transform": "translate(30%, -30%)", "pointer-events": "none"}}></span>
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: s.colorGlow, color: s.color, border: `1px solid ${s.colorGlow}` }}>
                <s.icon size={15} strokeWidth={1.8}/>
              </div>
              <span className={`stat-card-trend${s.trendDir === 'neutral' ? ' neutral' : ''}`} style={{ color: s.trendColor, background: `${s.trendColor}15`, border: `1px solid ${s.colorGlow}` }}>
                {s.change}
              </span>
            </div>
            <div className="stat-card-value">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div style={{ "position": "absolute", "bottom": "0px", "left": "0px", "right": "0px", "height": "2px", background: `linear-gradient(90deg,${s.color}88,transparent)`, "border-radius": "0 0 var(--radius-lg) var(--radius-lg)" }}></div>
          </div>
        ))}
      </div>

      <div className="charts-row">
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Revenue vs Expenses</div>
              <div className="card-subtitle">Full year comparison</div>
            </div>
            <div style={{ display:'flex', gap:12 }}>
              <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:'var(--fs-xs)', color:'var(--text3)' }}>
                <span style={{ width:8, height:8, borderRadius:2, background:'rgba(79,70,229,.8)', display:'inline-block' }}/>Revenue
              </span>
              <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:'var(--fs-xs)', color:'var(--text3)' }}>
                <span style={{ width:8, height:8, borderRadius:2, background:'rgba(239,68,68,.5)', display:'inline-block' }}/>Expenses
              </span>
            </div>
          </div>
          <div style={{ position:'relative', height:240 }}><canvas ref={barRef}/></div>
        </div>

        <div className="card">
          <div className="card-header"><div className="card-title">Revenue by Plan</div></div>
          <div style={{ position:'relative', height:160 }}><canvas ref={donutRef}/></div>
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:'var(--space-md)' }}>
            {[
              { l:'Enterprise', c:'#4F46E5', v:'28%' },
              { l:'Pro',        c:'#06B6D4', v:'42%' },
              { l:'Starter',    c:'#10B981', v:'22%' },
              { l:'Free',       c:'#475569', v:'8%' },
            ].map(d => (
              <div key={d.l} style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ width:8, height:8, borderRadius:2, background:d.c, flexShrink:0 }}/>
                <span style={{ flex:1, fontSize:'var(--fs-sm)', color:'var(--text2)' }}>{d.l}</span>
                <span style={{ fontSize:'var(--fs-sm)', fontWeight:600, color:'var(--text1)' }}>{d.v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .dash-page { display:flex; flex-direction:column; gap:var(--space-lg); width:100%; }
        .report-hero {
          position: relative;
          display: flex; justify-content: space-between; align-items: flex-end;
          gap: var(--space-md); flex-wrap: wrap;
          background: radial-gradient(120% 140% at 0% 0%, rgba(79,70,229,.22), rgba(6,182,212,.08));
          border-color: var(--border-active);
        }
        .report-hero-glow {
          position: absolute; width:220px; height:220px; right:-80px; top:-70px;
          border-radius: 50%;
          background: rgba(79,70,229,.2); filter: blur(40px); pointer-events: none;
        }
        .report-overline {
          font-size: var(--fs-xs); text-transform: uppercase; letter-spacing:.08em;
          color: var(--accent-hover); font-weight: 700; margin-bottom: 4px;
        }
      `}</style>
    </div>
  )
}